import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import {
    ReviewRating as ReviewRatingDesignSystem,
    ReviewRatingProps,
} from '../../../design-system/components/review/rating/ReviewRating';
import { Rating } from '../../../resources/rating/domain/types/rating';

import { ReviewRatingFallback } from './ReviewRatingFallback';

type Props = Omit<
    ReviewRatingProps,
    'fallback' | 'ratingValue' | 'maxRating' | 'minRating' | 'ariaLabel' | 'reviewsCount'
> & {
    rating: Rating;
    reviewsCount?: number;
    trackingInformation?: TrackingProperties<typeof ampli.testimonialClicked>;
};

export function ReviewRating({
    rating,
    reviewsCount,
    onClick,
    trackingInformation,
    ...props
}: Props) {
    const { trackEvent } = useTrackEventAmplitude();
    const { formatMessage } = useIntl();

    const handleClick = useCallback(() => {
        if (typeof onClick === 'function') {
            onClick();
        }

        if (trackingInformation) {
            trackEvent((ampli, defaultProperties) =>
                ampli.testimonialClicked({
                    ...defaultProperties,
                    ...trackingInformation,
                }),
            );
        }
    }, [onClick, trackEvent, trackingInformation]);

    return (
        <ReviewRatingDesignSystem
            {...props}
            ratingValue={rating.value}
            minRating={rating.min}
            maxRating={rating.max}
            onClick={handleClick}
            reviewsCount={
                typeof reviewsCount === 'number'
                    ? formatMessage(
                          { id: 'global.component.reviewRating.reviewsCount' },
                          { reviewsCount },
                      )
                    : undefined
            }
            ariaLabel={({ value, max }) => {
                return formatMessage(
                    {
                        id: 'global.component.reviewRating.iconsDescription',
                    },
                    { rating: value, maxRating: max },
                );
            }}
            fallback={(error) => {
                return <ReviewRatingFallback error={error} />;
            }}
        />
    );
}
